import React from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled';
import { Mutation } from '@apollo/client/react/components';
import { FormattedMessage } from 'react-intl';
import { Route } from 'react-router';
import { Link } from 'react-router-dom';

import ImageView from 'lib/images/image-view';
import SVGIconView from 'lib/images/svg-icon-view';

import TextShortener from 'four-nets/ui-v2/text-shortener/text-shortener';
import IconMember from 'four-nets/icons/svg/icon_members.svg';
import IconFeed from 'four-nets/icons/svg/icon_feed.svg';
import IconAddToGroup from 'four-nets/icons/svg/icon_add-to-group.svg';
import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';

import { GroupTileFragment } from '../new-groups.graphql';
import joinGroupMutation, { JoinGroupMutation, JoinGroupMutationVariables } from './mutation.graphql';
import defaultStyles from './group.scss';

export interface Props extends GroupTileFragment {
    styles: typeof defaultStyles;
}

class Group extends React.PureComponent<Props> {
    static contextTypes = {
        authCheck: PropTypes.func,
    };
    render() {
        const { styles } = this.props;
        return (
            <div className={styles.container}>
                <Link to={this.props.href} className={styles.link}>
                    <div className={styles.image}>
                        {this.props.coverPhoto ? (
                            <FormattedMessage id='GROUP_COVER_PHOTO'>
                                {msg => (
                                    <ImageView
                                        aspectFill={true}
                                        srcSet={this.props.coverPhoto!.srcset!}
                                        src={this.props.coverPhoto!.src}
                                        sizes={{ xs: 200 }}
                                        alt={`${this.props.name} - ${msg}`}
                                        linkify={false}
                                    />
                                )}
                            </FormattedMessage>
                        ) : null}
                    </div>
                    <h3 className={styles.groupName}>
                        <TextShortener text={this.props.name ? this.props.name : ''} textLength={40} />
                    </h3>
                </Link>
                <div className={styles.groupInfo}>
                    <p className={styles.groupDescription}>
                        <TextShortener text={this.props.description ? this.props.description : ''} textLength={140} />
                    </p>
                    <div className={styles.groupStats}>
                        <div>
                            <SVGIconView className={styles.statImage} src={IconMember} />
                            <FormattedMessage
                                id='FORUM_%(num)s_PRIVATE_THREAD_MEMBERS'
                                values={{ num: this.props.membersCount }}
                            />
                        </div>
                        <div>
                            <SVGIconView className={styles.statImage} src={IconFeed} />
                            <FormattedMessage
                                id='%(number)s_FORUM_MESSAGES'
                                values={{ number: this.props.postsCount }}
                            />
                        </div>
                    </div>
                    {this.props.isJoinedByViewer ? (
                        <form action={this.props.href}>
                            <button className={styles.enterGroupButton}>
                                <FormattedMessage id='ENTER_GROUP' />
                            </button>
                        </form>
                    ) : (
                        <Route>
                            {({ history }) => (
                                <Mutation<JoinGroupMutation, JoinGroupMutationVariables> mutation={joinGroupMutation}>
                                    {joinGroup => (
                                        <button
                                            className={styles.addToGroupButton}
                                            onClick={() =>
                                                this.context.authCheck(() => {
                                                    return joinGroup({
                                                        variables: {
                                                            input: {
                                                                id: this.props.id,
                                                            },
                                                        },
                                                    }).then(() => {
                                                        history.push(this.props.href);
                                                    });
                                                }, AuthOrigin.JoinGroup)
                                            }
                                        >
                                            <SVGIconView className={styles.addToGroupImage} src={IconAddToGroup} />
                                            <FormattedMessage id='GROUPS_ADD_TO_GROUP' />
                                        </button>
                                    )}
                                </Mutation>
                            )}
                        </Route>
                    )}
                </div>
            </div>
        );
    }
}

export default Styled(defaultStyles)(Group);
